import { useState, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiMapPin, FiUsers, FiSearch, FiAlertTriangle,
  FiMail, FiPhone, FiUser, FiCalendar, FiChevronDown, FiChevronUp, FiInfo,
} from 'react-icons/fi';
import { getDerasars, getUserProfile, joinDerasar, updateUserProfile } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { StateCitySelect } from '../../components/common';
import styles from './BrowseDerasar.module.css';

const formatDate = (d) =>
  d ? new Date(d + (d.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

const getDaysUntilExpiry = (endDate) => {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
};

const getInitials = (name) =>
  (name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();

const EMPTY_ADDRESS_FORM = {
  flatNo: '', buildingName: '', addressLine1: '', addressLine2: '',
  city: '', state: '', pincode: '', country: 'India', notes: '',
};

const REQUIRED_ADDRESS_FIELDS = [
  ['flatNo', 'Flat / Bunglow No.'],
  ['buildingName', 'Apartment / Bunglow Name'],
  ['addressLine1', 'Address Line 1'],
  ['city', 'City'],
  ['state', 'State'],
  ['pincode', 'Pincode'],
];

// -- Detail panel rendered as a <tr> below the main row ---------------------
const DetailRow = ({ d, colSpan }) => (
  <tr className={styles.detailRow}>
    <td colSpan={colSpan}>
      <div className={styles.detailPanel}>
        <div className={styles.detailGrid}>

          {/* Location */}
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}><FiMapPin size={11} /> Location</span>
            <span className={styles.detailValue}>{d.city || '—'}</span>
          </div>

          {/* Establishment Date */}
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}><FiCalendar size={11} /> Established</span>
            <span className={styles.detailValue}>{formatDate(d.establishDate) || '—'}</span>
          </div>

          {/* Registered Since */}
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}><FiCalendar size={11} /> Registered Since</span>
            <span className={styles.detailValue}>{formatDate(d.registeredSince) || '—'}</span>
          </div>

          {/* Total Persons */}
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}><FiUsers size={11} /> Total Persons</span>
            <span className={styles.detailValue}>{d.personCount ?? 0}</span>
          </div>

          {/* Admin Name */}
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}><FiUser size={11} /> Admin Name</span>
            <span className={styles.detailValue}>{d.admin?.name || '—'}</span>
          </div>

          {/* Admin Email */}
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}><FiMail size={11} /> Admin Email</span>
            <span className={styles.detailValue}>
              {d.admin?.email
                ? <a href={`mailto:${d.admin.email}`} className={styles.detailLink}>{d.admin.email}</a>
                : '—'}
            </span>
          </div>

          {/* Admin Phone */}
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}><FiPhone size={11} /> Admin Phone</span>
            <span className={styles.detailValue}>
              {d.admin?.phone
                ? <a href={`tel:${d.admin.phone}`} className={styles.detailLink}>{d.admin.phone}</a>
                : '—'}
            </span>
          </div>

          {/* Admin Address (from profile) */}
          {d.admin?.address && (
            <div className={`${styles.detailItem} ${styles.detailFullRow}`}>
              <span className={styles.detailLabel}><FiMapPin size={11} /> Derasar Address</span>
              <span className={styles.detailValue}>{d.admin.address}</span>
            </div>
          )}

          {/* Description */}
          {d.description && (
            <div className={`${styles.detailItem} ${styles.detailFullRow}`}>
              <span className={styles.detailLabel}><FiInfo size={11} /> Description</span>
              <span className={styles.detailValue}>{d.description}</span>
            </div>
          )}
        </div>
      </div>
    </td>
  </tr>
);

// -- Main component ----------------------------------------------------------
const BrowseDerasar = () => {
  const { user } = useAuth();
  const [derasars, setDerasars] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(null);
  const [sentRequests, setSentRequests] = useState(new Set());
  const [expandedId, setExpandedId] = useState(null);
  const [addressModal, setAddressModal] = useState(null);
  const [addressForm, setAddressForm] = useState(EMPTY_ADDRESS_FORM);
  const [addressLoading, setAddressLoading] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'platform_admin';

  useEffect(() => {
    getDerasars()
      .then((res) => setDerasars(res.data))
      .catch(() => toast.error('Failed to load derasars'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return derasars;
    return derasars.filter((d) => {
      const adminName    = (d.admin?.name    || '').toLowerCase();
      const adminEmail   = (d.admin?.email   || '').toLowerCase();
      const adminPhone   = (d.admin?.phone   || '').toLowerCase();
      const adminAddress = (d.admin?.address || '').toLowerCase();
      return (
        d.name.toLowerCase().includes(q) ||
        (d.city || '').toLowerCase().includes(q) ||
        adminName.includes(q) ||
        adminEmail.includes(q) ||
        adminPhone.includes(q) ||
        adminAddress.includes(q)
      );
    });
  }, [derasars, search]);

  // Derasar admins already have their own derasar — redirect them away
  if (user?.role === 'derasar_admin') return <Navigate to="/dashboard" replace />;

  const getMembership = (derasarId) =>
    (user?.derasars || []).find((d) => {
      const id = d.derasarId?.id || d.derasarId;
      return id?.toString() === derasarId;
    });

  const openAddressModal = async (derasarId, message) => {
    setAddressModal({ derasarId, message });
    setAddressLoading(true);
    try {
      const res = await getUserProfile();
      setAddressForm({ ...EMPTY_ADDRESS_FORM, ...(res.data || {}) });
    } catch {
      setAddressForm(EMPTY_ADDRESS_FORM);
    } finally {
      setAddressLoading(false);
    }
  };

  const closeAddressModal = () => {
    setAddressModal(null);
    setAddressForm(EMPTY_ADDRESS_FORM);
    setAddressLoading(false);
  };

  const validateAddressForm = () => {
    const missingField = REQUIRED_ADDRESS_FIELDS.find(([key]) => !String(addressForm[key] || '').trim());
    return missingField ? `${missingField[1]} is required.` : '';
  };

  const handleJoin = async (id) => {
    setJoining(id);
    try {
      await joinDerasar(id);
      toast.success('Join request sent! Waiting for approval.');
      setSentRequests((prev) => new Set([...prev, String(id)]));
      const res = await getDerasars();
      setDerasars(res.data);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to send request';
      if (message.toLowerCase().includes('profile address before joining')) {
        await openAddressModal(id, message);
      } else {
        toast.error(message);
      }
    } finally {
      setJoining(null);
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressForm((current) => ({ ...current, [name]: value }));
  };

  const handleSaveAddressAndContinue = async (e) => {
    e.preventDefault();
    const validationError = validateAddressForm();
    if (validationError) { toast.error(validationError); return; }
    if (!addressModal?.derasarId) return;
    setAddressLoading(true);
    try {
      await updateUserProfile(addressForm);
      await joinDerasar(addressModal.derasarId);
      toast.success('Address saved and join request sent.');
      setSentRequests((prev) => new Set([...prev, String(addressModal.derasarId)]));
      closeAddressModal();
      const res = await getDerasars();
      setDerasars(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save address and continue');
    } finally {
      setAddressLoading(false);
    }
  };

  const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

  const COL_COUNT = 7;

  return (
    <div>
      <div className="page-header">
        <h2>Browse Derasars</h2>
        <span style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
          {filtered.length} derasar{filtered.length !== 1 ? 's' : ''} found
        </span>
      </div>

      {/* -- Table card -- */}
      <div className={styles.sectionCard}>
        <div className={styles.searchBar}>
          <FiSearch className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search by name, city, admin, email or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="icon">??</div>
            <p>{search ? 'No derasars match your search' : 'No derasars available'}</p>
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.derasarTable}>
              <colgroup>
                <col style={{ width: '22%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '12%' }} />
                <col style={{ width: '10%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Derasar</th>
                  <th><FiMapPin size={11} style={{ marginRight: 4 }} />Location</th>
                  <th><FiUsers size={11} style={{ marginRight: 4 }} />Persons</th>
                  <th><FiUser size={11} style={{ marginRight: 4 }} />Admin</th>
                  <th><FiMail size={11} style={{ marginRight: 4 }} />Email</th>
                  <th><FiPhone size={11} style={{ marginRight: 4 }} />Phone</th>
                  <th className={styles.actionsCol}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const membership = getMembership(d.id);
                  const days = getDaysUntilExpiry(d.endDate);
                  const isExpired = days !== null && days < 0;
                  const isExpanded = expandedId === d.id;

                  return (
                    <>
                      <tr
                        key={d.id}
                        className={`${styles.tableRow} ${isExpanded ? styles.tableRowExpanded : ''}`}
                        onClick={() => toggleExpand(d.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        {/* Derasar Name */}
                        <td>
                          <div className={styles.nameCell}>
                            <div className={styles.derasarAvatar}>{getInitials(d.name)}</div>
                            <div className={styles.nameCellInfo}>
                              <span className={styles.derasarName}>{d.name}</span>
                            </div>
                            <span className={styles.expandIcon}>
                              {isExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                            </span>
                          </div>
                        </td>

                        {/* Location */}
                        <td className={styles.addressCell}>
                          {d.city || <span className={styles.muted}>—</span>}
                        </td>

                        {/* Persons count */}
                        <td>
                          <span className={styles.memberCount}>{d.personCount ?? 0}</span>
                        </td>

                        {/* Admin name */}
                        <td>
                          {d.admin?.name
                            ? <span className={styles.adminName}>{d.admin.name}</span>
                            : <span className={styles.muted}>—</span>
                          }
                        </td>

                        {/* Admin email */}
                        <td className={styles.contactCell}>
                          {d.admin?.email
                            ? <span title={d.admin.email}>{d.admin.email}</span>
                            : <span className={styles.muted}>—</span>
                          }
                        </td>

                        {/* Admin phone */}
                        <td className={styles.contactCell}>
                          {d.admin?.phone || <span className={styles.muted}>—</span>}
                        </td>

                        {/* Action */}
                        <td className={styles.actionsCol} onClick={(e) => e.stopPropagation()}>
                          {isSuperAdmin ? (
                            <span className={`badge badge-${d.subscriptionStatus === 'active' ? 'approved' : 'pending'}`}>
                              {d.subscriptionStatus || 'approved'}
                            </span>
                          ) : membership ? (
                            <span className={`badge badge-${membership.status}`}>
                              {membership.status === 'approved' ? 'Member' : membership.status}
                            </span>
                          ) : sentRequests.has(String(d.id)) ? (
                            <span className="badge badge-pending">Request Sent</span>
                          ) : (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleJoin(d.id)}
                              disabled={joining === d.id || isExpired}
                              title={isExpired ? 'Derasar subscription has expired' : undefined}
                            >
                              {joining === d.id ? 'Sending…' : 'Join'}
                            </button>
                          )}
                        </td>
                      </tr>

                      {/* -- Expanded detail row -- */}
                      {isExpanded && (
                        <DetailRow key={`detail-${d.id}`} d={d} colSpan={COL_COUNT} />
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* -- Address modal -- */}
      {addressModal && (
        <div className="modal-overlay" onClick={closeAddressModal}>
          <div className={`${styles.addressModal} modal`} onClick={(e) => e.stopPropagation()}>
            <h3>Complete Address Before Joining</h3>
            <p className={styles.addressModalMessage}>{addressModal.message}</p>
            <form onSubmit={handleSaveAddressAndContinue}>
              <div className={styles.addressGrid}>
                <div className="form-group">
                  <label>Flat / Bunglow No. *</label>
                  <input name="flatNo" value={addressForm.flatNo} onChange={handleAddressChange} placeholder="e.g. A-101" disabled={addressLoading} />
                </div>
                <div className="form-group">
                  <label>Apartment / Bunglow Name *</label>
                  <input name="buildingName" value={addressForm.buildingName} onChange={handleAddressChange} placeholder="e.g. Shanti Niketan" disabled={addressLoading} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Address Line 1 *</label>
                  <input name="addressLine1" value={addressForm.addressLine1} onChange={handleAddressChange} placeholder="Street / Area / Locality" disabled={addressLoading} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Address Line 2</label>
                  <input name="addressLine2" value={addressForm.addressLine2} onChange={handleAddressChange} placeholder="Landmark / Additional info" disabled={addressLoading} />
                </div>
                <StateCitySelect
                  state={addressForm.state}
                  city={addressForm.city}
                  onStateChange={(val) => setAddressForm(f => ({ ...f, state: val, city: '' }))}
                  onCityChange={(val) => setAddressForm(f => ({ ...f, city: val }))}
                  disabled={addressLoading}
                  required
                />
                <div className="form-group">
                  <label>Pincode *</label>
                  <input name="pincode" value={addressForm.pincode} onChange={handleAddressChange} placeholder="e.g. 380001" disabled={addressLoading} />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input name="country" value={addressForm.country} onChange={handleAddressChange} placeholder="India" disabled={addressLoading} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={closeAddressModal} disabled={addressLoading}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={addressLoading}>
                  {addressLoading ? 'Saving…' : 'Save & Continue'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrowseDerasar;
